import type { RefObject } from "react";
import { useCallback } from "react";
import { type CameraBookmark, useStore } from "../app/store.ts";
import type { CameraProjection } from "../app/storeTypes.ts";
import {
  DEFAULT_CAMERA_POS,
  DEFAULT_CAMERA_TARGET,
} from "../viewport/Viewport3D.tsx";
import { Button, IconButton } from "./Button.tsx";
import { CameraIcon } from "./Icons.tsx";
import { Popover } from "./Popover.tsx";
import { RenderDimensionsControls } from "./RenderDimensionsControls.tsx";
import {
  Divider,
  PanelHeader,
  SectionLabel,
  SegmentedControl,
  SliderRow,
} from "./shared.tsx";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG_SUBTLE,
  COLOR_BORDER,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_MUTED,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_PANEL_TITLE,
  FONT_SIZE_SMALL,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// CameraPanel
// ---------------------------------------------------------------------------

export function CameraPanel({
  anchorRef,
  open,
  onClose,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
}) {
  const focalLength = useStore((s) => s.focalLength);
  const setFocalLength = useStore((s) => s.setFocalLength);
  const bookmarks = useStore((s) => s.cameraBookmarks);
  const addBookmark = useStore((s) => s.addCameraBookmark);
  const removeBookmark = useStore((s) => s.removeCameraBookmark);
  const focusSelection = useStore((s) => s.focusSelection);
  const hasSelection = useStore((s) => s.selectedObjectTargets.length > 0);
  const isRenderMode = useStore((s) => s.workspaceMode === "render");
  const isInkingMode = useStore((s) => s.workspaceMode === "inking");
  const showDimensions = isRenderMode || isInkingMode;

  const cameraProjection = useStore((s) => s.cameraProjection);
  const setCameraProjection = useStore((s) => s.setCameraProjection);

  const handleSaveBookmark = useCallback(() => {
    const { cameraPosition, cameraTarget } = useStore.getState();
    const idx = bookmarks.length + 1;
    addBookmark(`Camera ${idx}`, cameraPosition, cameraTarget);
  }, [addBookmark, bookmarks.length]);

  const handleRestoreBookmark = useCallback((bm: CameraBookmark) => {
    const s = useStore.getState();
    s.setCameraOrbit(bm.position, bm.target);
    s.setFocalLength(bm.focalLength);
  }, []);

  return (
    <Popover
      anchorRef={anchorRef}
      open={open}
      onClose={onClose}
      style={{ width: 320, padding: "16px 20px" }}
    >
      <PanelHeader icon={<CameraIcon />}>Camera</PanelHeader>

      {/* Projection mode */}
      <SectionLabel>Projection</SectionLabel>
      <SegmentedControl<CameraProjection>
        options={[
          { label: "Perspective", value: "perspective" },
          { label: "Orthographic", value: "orthographic" },
        ]}
        value={cameraProjection}
        onChange={setCameraProjection}
      />

      {/* Focal length slider — only meaningful for perspective */}
      {cameraProjection === "perspective" && (
        <div style={{ marginTop: 8 }}>
          <SliderRow
            label="Focal length"
            value={focalLength}
            min={15}
            max={200}
            displayValue={`${focalLength}mm`}
            onChange={setFocalLength}
          />
        </div>
      )}

      {/* Output dimensions — visible in render and inking modes */}
      {showDimensions && (
        <>
          <Divider />
          <SectionLabel>Output Dimensions</SectionLabel>
          <RenderDimensionsControls />
        </>
      )}

      {/* Camera actions */}
      <Divider />
      <div style={{ display: "flex", gap: 6 }}>
        <Button
          onClick={() => {
            const s = useStore.getState();
            s.setCameraOrbit(DEFAULT_CAMERA_POS, DEFAULT_CAMERA_TARGET);
            s.setFocalLength(50);
          }}
          title="Reset camera to default position"
          variant="secondary"
          fullWidth
        >
          Reset
        </Button>
        <Button
          onClick={focusSelection}
          disabled={!hasSelection}
          title="Set orbit target to the center of the selected object(s)"
          variant="secondary"
          fullWidth
        >
          Focus Selection
        </Button>
      </div>

      {/* Camera bookmarks */}
      <Divider />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_PANEL_TITLE,
            fontWeight: 500,
            color: COLOR_TEXT_MUTED,
          }}
        >
          Saved Views
        </span>
        <Button
          onClick={handleSaveBookmark}
          title="Save current camera position"
          variant="secondary"
          size="xs"
          style={{
            padding: "4px 10px",
            background: COLOR_ACTIVE_BG_SUBTLE,
            color: COLOR_ACCENT,
          }}
        >
          + Save
        </Button>
      </div>

      {bookmarks.length === 0 && (
        <div
          style={{
            fontSize: FONT_SIZE_BODY,
            color: COLOR_TEXT_DISABLED,
            padding: "4px 0",
          }}
        >
          No saved views yet
        </div>
      )}

      {bookmarks.map((bm) => (
        <BookmarkRow
          key={bm.id}
          bookmark={bm}
          onRestore={handleRestoreBookmark}
          onRemove={removeBookmark}
        />
      ))}
    </Popover>
  );
}

function BookmarkRow({
  bookmark,
  onRestore,
  onRemove,
}: {
  bookmark: CameraBookmark;
  onRestore: (bm: CameraBookmark) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 0",
      }}
    >
      <Button
        onClick={() => onRestore(bookmark)}
        title={`Restore "${bookmark.name}" (${bookmark.focalLength}mm)`}
        variant="secondary"
        style={{
          flex: 1,
          padding: "7px 10px",
          border: `0.5px solid ${COLOR_BORDER}`,
          color: COLOR_TEXT_SECONDARY,
          textAlign: "left",
          justifyContent: "space-between",
        }}
      >
        <span>{bookmark.name}</span>
        <span style={{ fontSize: FONT_SIZE_SMALL, color: COLOR_TEXT_FAINT }}>
          {bookmark.focalLength}mm
        </span>
      </Button>
      <IconButton
        onClick={() => onRemove(bookmark.id)}
        title="Remove saved view"
        label="Remove saved view"
        icon="×"
        variant="ghost"
        style={{
          border: `0.5px solid ${COLOR_BORDER}`,
          color: COLOR_TEXT_FAINT,
        }}
      />
    </div>
  );
}
